/**
 * activityFilters — pure helpers for the game log's filters (player, event
 * kind, sort; components/game/GameLogFilterPanel), shared by the in-game log
 * and its grown sheet. `describeActiveFilters` labels what's active, e.g. for
 * the filter button's summary. Kept framework-free for unit testing.
 */

export interface ActivityFilterState {
  /** Slot string of the player filter, or null for "all players". */
  playerFilter: string | null
  /** Event-kind key of the kind filter, or null for "all events". */
  kindFilter: string | null
  /** Sort order. true → newest-first (the default; not summarised). */
  newestFirst: boolean
}

/**
 * Build the ordered list of active-filter labels shown when the filter panel
 * is collapsed. Returns an empty array when nothing but the default sort is
 * active (→ the collapsed header reads plainly, e.g. "Filters").
 *
 * `playerLabel` / `kindLabel` resolve the raw slot/kind keys to display text;
 * both fall back to a readable default so the helper is usable standalone.
 * Only the non-default sort ("Oldest first") is surfaced — newest-first is the
 * baseline and would be noise.
 */
export function describeActiveFilters(args: {
  playerFilter: string | null
  kindFilter: string | null
  newestFirst: boolean
  playerLabel?: (slot: string) => string
  kindLabel?: (kind: string) => string
}): string[] {
  const out: string[] = []
  if (args.playerFilter) {
    out.push(args.playerLabel?.(args.playerFilter) ?? `Player ${args.playerFilter}`)
  }
  if (args.kindFilter) {
    out.push(args.kindLabel?.(args.kindFilter) ?? args.kindFilter)
  }
  if (!args.newestFirst) out.push('Oldest first')
  return out
}

/** True iff any non-default filter is active. */
export function hasActiveFilters(args: {
  playerFilter: string | null
  kindFilter: string | null
  newestFirst: boolean
}): boolean {
  return args.playerFilter !== null || args.kindFilter !== null || !args.newestFirst
}

/**
 * Event kinds the in-game log's filter offers, in chip order. Chat is not one:
 * the log's ALL | CHAT toggle owns it.
 */
export const LOG_FILTER_KINDS = ['claim', 'attack', 'fortify', 'elimination', 'game-end'] as const
export type LogFilterKind = (typeof LOG_FILTER_KINDS)[number]
export const LOG_FILTER_KIND_LABEL: Record<LogFilterKind, string> = {
  claim: 'Claim',
  attack: 'Attack',
  fortify: 'Fortify',
  elimination: 'Elim',
  'game-end': 'Game End',
}

export const DEFAULT_LOG_FILTERS: ActivityFilterState = { playerFilter: null, kindFilter: null, newestFirst: true }

/** Chat-only chips include authors, regardless of the currently selected filter. */
export function chatFilterPlayers<P extends { userId: string }>(
  players: readonly P[],
  messages: readonly { userId: string }[],
): P[] {
  const authors = new Set(messages.map(message => message.userId))
  return players.filter(player => authors.has(player.userId))
}

/** Drop an ALL-view player selection when that player has no visible chat. */
export function chatAuthorPlayerFilter(selected: string | null, authors: readonly { slot: number }[]): string | null {
  return selected !== null && authors.some(player => String(player.slot) === selected) ? selected : null
}

/**
 * Apply the log filters to newest-first rows. A kind filter keeps only event
 * rows of that kind (chat lines carry no kinds, so they drop out); a player
 * filter keeps rows involving that slot, chat by its author included.
 */
export function applyLogFilters<R extends { kinds: readonly string[]; relatedPlayerIds: readonly string[] }>(
  rowsNewestFirst: readonly R[],
  filters: ActivityFilterState,
): R[] {
  let rows = rowsNewestFirst as R[]
  if (filters.playerFilter !== null) rows = rows.filter((row) => row.relatedPlayerIds.includes(filters.playerFilter!))
  if (filters.kindFilter !== null) rows = rows.filter((row) => row.kinds.includes(filters.kindFilter!))
  return filters.newestFirst ? rows : [...rows].reverse()
}

/** The kinds present in `rows`, in LOG_FILTER_KINDS order (chips only for what exists). */
export function presentLogKinds(rows: readonly { kinds: readonly string[] }[]): LogFilterKind[] {
  const seen = new Set(rows.flatMap((row) => row.kinds))
  return LOG_FILTER_KINDS.filter((kind) => seen.has(kind))
}
